import { ErrorStatus } from 'redux-and-the-rest';

import { Error } from '../models/error';

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase().concat(string.slice(1));
}

function isRelationshipAttributeError(errorPointerPathParts: string[]) {
  return errorPointerPathParts.length === 6;
}

export class ErrorMap<T extends string = ''> extends Map<T, Error> {
}
/**
 * Converts an array of redux-and-the-rest errors into a map of webservice Errors keyed by the property name on the
 * originalError's pointer.
 * @param {ErrorStatus[]} errors The redux-and-the-rest resource errors
 * @returns {[key: string]: Error} An object keyed by string values of webservice Errors
 */
export function buildErrorsDictionary<T extends string>(errors: ErrorStatus[] | undefined): ErrorMap<T> {
  const output = new ErrorMap<T>();
  if (errors) {
    errors.forEach((error) => {
      const originalError: Error = error.originalError as Error;
      /**
       * Check the presence of an originalError. In the case of 401 respones, we can get an empty body and an undefined
       * object here.
       */
      if (originalError) {
        /**
         * Entity attribute errors:
         * source.pointer: "/data/attributes/[attribute name]"
         *
         * Entity relationship attribute errors:
         * source.pointer: "/data/relationships/[relationship name]/0/attributes/[attribute name]"
         *
         * Within this project, relationship attributes within a form are named with the
         * convention relationshipAttribute
         * ie schoolsName
         */
        const splitPointer = originalError.source.pointer.split('/');

        if (isRelationshipAttributeError(splitPointer)) {
          const attributeName = splitPointer[splitPointer.length - 1];
          const relationshipName = splitPointer[2];
          const fieldName = `${relationshipName}${capitalizeFirstLetter(attributeName)}`;

          output.set(fieldName as T, originalError);
        } else {
          // Entity attribute error
          output.set(splitPointer[splitPointer.length - 1] as T, originalError);
        }
      }
    });
  }
  return output;
}

export function formatError<T extends string>(dictionary: ErrorMap<T>, key: T): string | undefined {
  return dictionary.get(key as T)?.detail;
}
